import { Box, Grid, useTheme } from "@material-ui/core";
import React, { ReactChild, memo, useMemo } from "react";
import BarChartNoData from "~/components/placeholders/barChartNoData";
import { DATETIME_PERIODS, INTERVAL } from "~/store/utils/dateTimeUtils";
import Panel from "~/components/panel/panel";
import PanelLoading from "~/components/loadingIndicator/panelLoadingIndicator";
import {
  CurrencyRate,
  Range,
  Filter,
  ShippedRevenueTrend,
  MarketplaceFilters,
} from "~/typedef/store";
import {
  useShippedTrendQuery,
  useShippedRevenueTotalsQuery,
} from "~/store/mystore/vendor.redux";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";
import GenericTotal from "~/components/totals/genericTotal";
import TotalSales from "~/components/totals/totalSales";
import { getConvertedValue } from "~/utils/currencyUtils";
import { marketplaceLink } from "~/utils/marketplaceUtils";
import moment from "moment";
import { DEFAULT_CURRENCY } from "~/store/persistentAppSettings.redux";
import { User } from "~/typedef/user";
import { useForecastingValuesQuery } from "~/store/mystore/forecasting.redux";
import {
  ForecastDataItem,
  FORECAST_TYPE,
  REFERENCE_LEVEL,
} from "~/components/forecasting/types";
import MultiBarLineChart, {
  MultiBarLineChartProps,
} from "~/components/charts/multiBarLineChart/multiBarLineChart";
import { CHART_TITLES } from "~/components/charts/chartUtils/chartUtils";
import { determineForecastTimestamp } from "../widgets/salesPerformanceLineChart";

export interface ChartOption {
  value: string;
  label: string;
}

export const CHART_TYPE = {
  Revenue: "revenue",
  COGS: "cogs",
  NetPureProductMargin: "netPureProductMargin",
  Units: "units",
};

interface ShippedRevenueParams {
  userInfo: User;
  mid?: string;
  marketplaceFilters?: MarketplaceFilters;
  displayTotals?: boolean;
  currentFilter?: Filter;
  currentPeriod: DATETIME_PERIODS;
  currentRange: Range;
  currentCurrency: string;
  chartType: ChartOption;
  actions?: ReactChild;
  view: "sourcing" | "manufacturing";
  condensed?: boolean;
  report?: boolean;
  timezone: string;
  isLineChart?: boolean;
  isForecast?: boolean;
}

const formatShippedRevenueData = (
  shippedRevenueData: ShippedRevenueTrend | undefined,
  currentCurrency: string,
  currency = DEFAULT_CURRENCY,
  currencyRates: CurrencyRate[],
  chartType: ChartOption,
  showForecastingData: boolean,
  forecastData:
    | ForecastDataItem
    | {
        forecastValue: null;
        totals: null;
      },
  forecastDataTimezone: string,
  isForecast?: boolean
) => {
  if (!shippedRevenueData) {
    return {
      manufacturing: [],
      sourcing: [],
    };
  }

  const type = chartType?.value || CHART_TYPE.Revenue;
  const manufacturing = shippedRevenueData.manufacturing;
  const sourcing = shippedRevenueData.sourcing;
  const chartForecastData: {
    forecastTarget?: number | null;
    budgetTarget?: number | null;
  } = {
    forecastTarget: null,
    budgetTarget: null,
  };

  const formattedManufacturing =
    manufacturing?.length > 0
      ? manufacturing.map((data) => {
          if (showForecastingData && data.startTime) {
            const forecastTimestamp = determineForecastTimestamp(
              data.startTime,
              forecastDataTimezone,
              forecastData
            );

            if (isForecast) {
              const forecastTarget =
                forecastData?.forecastValue?.[forecastTimestamp]?.[
                  FORECAST_TYPE.FORECAST
                ]?.[REFERENCE_LEVEL.TARGET];
              if (Number.isFinite(forecastTarget?.amount)) {
                chartForecastData.forecastTarget = getConvertedValue(
                  currencyRates,
                  forecastTarget?.currency,
                  currentCurrency,
                  forecastTarget?.amount
                );
              }
            } else {
              const budgetTarget =
                forecastData?.forecastValue?.[forecastTimestamp]?.[
                  FORECAST_TYPE.BUDGET
                ]?.[REFERENCE_LEVEL.TARGET];
              if (Number.isFinite(budgetTarget?.amount)) {
                chartForecastData.budgetTarget = getConvertedValue(
                  currencyRates,
                  budgetTarget?.currency,
                  currentCurrency,
                  budgetTarget?.amount
                );
              }
            }
          }
          switch (type) {
            case CHART_TYPE.Revenue:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.current[type]
                    )
                  : data.current?.[type],
                prior: data.prior?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.prior[type]
                    )
                  : data.prior?.[type],
                ...chartForecastData,
              };
            case CHART_TYPE.COGS:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.current[type]
                    )
                  : data.current?.[type],
                prior: data.prior?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.prior[type]
                    )
                  : data.prior?.[type],
              };
            default:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type],
                prior: data.prior?.[type],
              };
          }
        })
      : [];

  const formattedSourcing =
    sourcing?.length > 0
      ? sourcing.map((data) => {
          if (showForecastingData && data.startTime) {
            const forecastTimestampNoOffset = moment
              .unix(data.startTime)
              .tz(forecastDataTimezone)
              .startOf("day")
              .unix();
            // For timezones with daylight savings
            const forecastTimestampPlusOneOffset = moment
              .unix(forecastTimestampNoOffset)
              .tz(forecastDataTimezone)
              .add(1, "hours")
              .startOf("day")
              .unix();
            const forecastTimestampMinusOneOffset = moment
              .unix(forecastTimestampNoOffset)
              .tz(forecastDataTimezone)
              .subtract(1, "hours")
              .startOf("day")
              .unix();
            const forecastTimestamp = forecastData?.forecastValue?.[
              forecastTimestampNoOffset
            ]
              ? forecastTimestampNoOffset
              : forecastData?.forecastValue?.[forecastTimestampPlusOneOffset]
              ? forecastTimestampPlusOneOffset
              : forecastData?.forecastValue?.[forecastTimestampMinusOneOffset]
              ? forecastTimestampMinusOneOffset
              : forecastTimestampNoOffset;

            if (isForecast) {
              const forecastTarget =
                forecastData?.forecastValue?.[forecastTimestamp]?.[
                  FORECAST_TYPE.FORECAST
                ]?.[REFERENCE_LEVEL.TARGET];
              if (Number.isFinite(forecastTarget?.amount)) {
                chartForecastData.forecastTarget = getConvertedValue(
                  currencyRates,
                  forecastTarget?.currency,
                  currentCurrency,
                  forecastTarget?.amount
                );
              }
            } else {
              const budgetTarget =
                forecastData?.forecastValue?.[forecastTimestamp]?.[
                  FORECAST_TYPE.BUDGET
                ]?.[REFERENCE_LEVEL.TARGET];
              if (Number.isFinite(budgetTarget?.amount)) {
                chartForecastData.budgetTarget = getConvertedValue(
                  currencyRates,
                  budgetTarget?.currency,
                  currentCurrency,
                  budgetTarget?.amount
                );
              }
            }
          }
          switch (type) {
            case CHART_TYPE.Revenue:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.current[type]
                    )
                  : data.current?.[type],
                prior: data.prior?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.prior[type]
                    )
                  : data.prior?.[type],
                ...chartForecastData,
              };
            case CHART_TYPE.COGS:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.current[type]
                    )
                  : data.current?.[type],
                prior: data.prior?.[type]
                  ? getConvertedValue(
                      currencyRates,
                      currency,
                      currentCurrency,
                      data.prior[type]
                    )
                  : data.prior?.[type],
              };
            default:
              return {
                startTime: data.startTime,
                endTime: data.endTime,
                current: data.current?.[type],
                prior: data.prior?.[type],
              };
          }
        })
      : [];

  return {
    manufacturing: formattedManufacturing,
    sourcing: formattedSourcing,
  };
};

const ShippedRevenueTrend = memo<ShippedRevenueParams>(
  function ShippedRevenueTrend({
    userInfo,
    mid,
    currentPeriod,
    currentRange,
    currentCurrency,
    chartType,
    actions,
    view,
    condensed,
    report,
    timezone,
    isLineChart,
    currentFilter,
    marketplaceFilters,
    displayTotals = false,
    isForecast,
  }) {
    const showSingleChannelTrend = !!mid;

    const { t } = useTranslation();
    const theme = useTheme();
    const { interval } = currentRange;

    const showForecastingData =
      chartType?.value === "revenue" && mid && interval !== INTERVAL.HOURS
        ? true
        : false;

    const currencyRates = useTypedSelector((state) =>
      state.globalVar ? state.globalVar.currencyRates : []
    );

    const totals = useShippedRevenueTotalsQuery(
      {
        currentRange,
        currentFilter,
        marketplaceFilters,
      },
      {
        selectFromResult: ({ data, isFetching }) => {
          return {
            shippedRevenueTotals: data,
            isLoading: isFetching,
            lastReportDate: data?.lastReportDate,
            lastUpdatedAt: data?.lastUpdatedAt,
            currency: data?.currency,
          };
        },
        skip: !displayTotals,
      }
    );

    const { forecastData, forecastDataTimezone, forecastDataLoading } =
      useForecastingValuesQuery(
        {
          mid: mid ?? "",
          currentRange,
          respectTimezone: true,
          includeInterval: true,
        },
        {
          skip: !showForecastingData,
          selectFromResult: ({ data, isFetching }) => ({
            forecastData: data?.data || { forecastValue: null, totals: null },
            forecastDataTimezone: data?.dataTimezone || currentRange.timezone,
            forecastDataLoading: isFetching,
          }),
        }
      );

    const { chartData, lastReportDate, lastUpdatedAt, isLoading } =
      useShippedTrendQuery(
        {
          mid,
          currentRange,
          currentFilter,
          marketplaceFilters,
        },
        {
          selectFromResult: ({ data, isFetching }) => ({
            isLoading: isFetching,
            lastReportDate: data?.lastReportDate,
            lastUpdatedAt: data?.lastUpdatedAt,
            chartData: formatShippedRevenueData(
              data?.revenueChartData,
              currentCurrency,
              data?.currency,
              currencyRates,
              chartType,
              showForecastingData,
              forecastData,
              forecastDataTimezone,
              isForecast
            ),
          }),
        }
      );

    const footerLink =
      showSingleChannelTrend &&
      condensed &&
      userInfo.brandAnalyticsOn &&
      !report
        ? {
            url: marketplaceLink("amazon_vendor", mid, "shippedrevenue"),
            label: t("generic.viewAllLink"),
          }
        : undefined;

    const getTotals = () => {
      if (!totals) return;

      const { currency, isLoading, shippedRevenueTotals } = totals;

      if (isLoading || !shippedRevenueTotals) return;

      const totalsByView = shippedRevenueTotals[view];

      switch (chartType.value) {
        case CHART_TYPE.Revenue:
          return (
            <TotalSales
              {...totalsByView.shippedRevenue}
              currentPeriod={currentPeriod}
              currency={currency}
              customTitle={t("shippedRevenueTrend.totalRevenueTitle")}
              currentCurrency={currentCurrency}
            />
          );

        case CHART_TYPE.COGS:
          return (
            <TotalSales
              {...totalsByView.shippedCogs}
              currency={currency}
              currentPeriod={currentPeriod}
              customTitle={t("shippedRevenueTrend.totalCoggsTitle")}
              currentCurrency={currentCurrency}
            />
          );

        case CHART_TYPE.Units:
          return (
            <GenericTotal
              number={totalsByView.shippedUnits.current}
              comparison={{
                prior: totalsByView.shippedUnits.prior,
              }}
              title={t(`shippedRevenueTrend.totalUnitsTitle`)}
            />
          );

        case CHART_TYPE.NetPureProductMargin:
          return (
            <GenericTotal
              number={totalsByView.netPureProductMargin.current}
              comparison={{
                prior: totalsByView.netPureProductMargin.prior,
              }}
              title={t(`shippedRevenueTrend.netPPMTitle`)}
              percentage
            />
          );
      }
    };

    const chartProps = useMemo(() => {
      const currentAndPrior = [
        {
          key: "current",
          colour: theme.palette.primary.main,
          toFixed: 2,
          isCurrency: [CHART_TYPE.Revenue].includes(chartType?.value),
          axis: "1" as const,
          unit: chartType?.value === CHART_TYPE.NetPureProductMargin ? "%" : "",
        },
        {
          key: "prior",
          colour: theme.palette.secondary.main,
          toFixed: 2,
          isCurrency: [CHART_TYPE.Revenue].includes(chartType?.value),
          axis: "1" as const,
          unit: chartType?.value === CHART_TYPE.NetPureProductMargin ? "%" : "",
        },
      ];

      if (showForecastingData) {
        isForecast
          ? currentAndPrior.push({
              key: "forecastTarget",
              colour: theme.palette.success.dark,
              toFixed: 2,
              isCurrency: true,
              axis: "1" as const,
              unit: "",
            })
          : currentAndPrior.push({
              key: "budgetTarget",
              colour: theme.palette.info.light,
              toFixed: 2,
              isCurrency: true,
              axis: "1" as const,
              unit: "",
            });
      }

      const chartProps: MultiBarLineChartProps = {
        title: t(CHART_TITLES[currentPeriod]),
        currentPeriod,
        currentCurrency,
        currencyRates,
        chartData: chartData[view],
        isLoading: false, // We have a loading check in our content
        lines: [],
        bars: [],
        xKey: "startTime",
        timezone,
        interval,
        tooltipPercentageChange: [
          {
            dataKey1: "current",
            dataKey2: "prior",
            toFixedDigits: 0,
          },
        ],
        tooltipAttainment: [],
      };

      if (showForecastingData && chartProps && chartProps.tooltipAttainment) {
        chartProps.tooltipAttainment.push({
          currentValueKey: "current",
          thresholdValueKey: isForecast ? "forecastTarget" : "budgetTarget",
          lowThreshold: 100,
          labelKey: "chartKeys.achievement",
        });
      }
      if (isLineChart) {
        chartProps.lines.push(...currentAndPrior);
      } else {
        chartProps.bars.push(...currentAndPrior);
      }

      return chartProps;
    }, [
      isLineChart,
      theme,
      t,
      formatShippedRevenueData,
      chartData,
      isForecast,
      view,
      forecastData,
    ]);

    return (
      <Panel
        id="widget-sales-performance"
        title={
          report
            ? `${t("chartTitle.ShippedRevenueTrend")} - ${chartType.label}`
            : t("chartTitle.ShippedRevenueTrend")
        }
        subtitle={
          lastReportDate && lastUpdatedAt
            ? `${t(`retailAnalytics.${view}View`)} - ${t(
                "retailAnalytics.updated",
                {
                  lastReportDate: moment(lastReportDate).format("ll"),
                  lastUpdatedAt: moment(lastUpdatedAt).format("ll"),
                }
              )}`
            : `${t(`retailAnalytics.${view}View`)}`
        }
        tooltip={
          showSingleChannelTrend
            ? t("shippedRevenueTrend.mainTooltip")
            : t("shippedRevenueTrend.multiChannelMainTooltip")
        }
        actions={actions}
        footerLink={footerLink}
        content={
          totals?.isLoading || isLoading || forecastDataLoading ? (
            <PanelLoading />
          ) : !chartData?.manufacturing.length ||
            !chartData?.sourcing.length ? (
            <BarChartNoData {...{ currentPeriod: currentPeriod }} />
          ) : (
            <Box p={2}>
              <Grid container spacing={1}>
                <Grid
                  container
                  item
                  xs={12}
                  md={displayTotals ? 9 : undefined}
                  lg={displayTotals ? 10 : undefined}
                >
                  <MultiBarLineChart {...chartProps} />
                </Grid>
                {!!totals && (
                  <Grid
                    container
                    item
                    xs={12}
                    md={3}
                    lg={2}
                    alignItems="center"
                  >
                    {getTotals()}
                  </Grid>
                )}
              </Grid>
            </Box>
          )
        }
      />
    );
  }
);

export default ShippedRevenueTrend;
